import { useState } from "react";
import { generatePath, useOutletContext } from "react-router";
import { styled } from "@pigment-css/react";
import { useTranslations } from "@acto/web-common/locales";
import ActoRoutes from "@acto/taskpane/core/router/routes.config";
import {
  Card,
  KpiHeadline,
  MetaLabel,
  RowListEmpty,
  RowSkeletonList,
} from "@acto/taskpane/field-sales-2/components/primitives/Card";
import { Divider } from "@acto/taskpane/field-sales-2/components/primitives/Divider";
import { Skeleton } from "@acto/taskpane/field-sales-2/components/primitives/Skeleton";
import { PageLayout } from "@acto/taskpane/field-sales-2/components/patterns/PageLayout";
import { OrderRows } from "@acto/taskpane/field-sales-2/components/patterns/OrderRows";
import { YearFilterPill } from "@acto/taskpane/field-sales-2/components/patterns/YearFilterPill/YearFilterPill";
import { useClientOrders } from "@acto/taskpane/field-sales-2/data-access/useClientOrders";
import { AnalyticsEvents, useTrack, useViewTrackedOnce } from "@acto/taskpane/core/analytics";
import { useNavigateWithReturn } from "@acto/taskpane/field-sales-2/navigation";
import type { CustomerOutletContext } from "@acto/taskpane/field-sales-2/CustomerDetailsLayout";
import { ShellSlotContent } from "@acto/taskpane/field-sales-2/shell";
import { useInfiniteScroll } from "./useInfiniteScroll";

const CURRENT_YEAR = new Date().getFullYear();

const SectionHeading = styled("h2")(({ theme }) => ({
  margin: 0,
  fontFamily: theme.fieldSalesTwo.font.family,
  fontSize: theme.fieldSalesTwo.font.size[18],
  fontWeight: theme.fieldSalesTwo.font.weight[500],
  lineHeight: 1.32,
  color: theme.fieldSalesTwo.colors.neutral[900],
}));

export function OrdersOverview() {
  const t = useTranslations("fieldSalesTwo");
  const push = useNavigateWithReturn();

  const { customer, isPending: customerPending } = useOutletContext<CustomerOutletContext>();
  const track = useTrack();

  useViewTrackedOnce(() => {
    if (!customer) return;
    track(AnalyticsEvents.ORDERS_VIEWED, { customerId: customer.id, customerName: customer.name });
  }, customer?.id ?? null);

  // Years that have orders, newest first, straight from the API. Until the user picks, show the most
  // recent; a stale pick (after switching customers) falls back to it.
  const yearOptions = customer?.orderYears ?? [];
  const [selectedYear, setSelectedYear] = useState<number | null>(null);
  const effectiveYear =
    selectedYear != null && yearOptions.includes(selectedYear) ? selectedYear : (yearOptions[0] ?? CURRENT_YEAR);
  const yearRange = { startDate: `${effectiveYear}-01-01`, endDate: `${effectiveYear}-12-31` };

  const { items, isPending, isLoading, isEmpty, hasMore, isLoadingMore, loadMore, formattedTotalIncomingOrder } =
    useClientOrders(customer?.id, yearRange);

  const sentinelRef = useInfiniteScroll<HTMLLIElement>({ hasMore, isLoadingMore, loadMore, resetKey: items.length });

  const handleOpenOrder = (orderId: string) => {
    if (!customer) return;
    track(AnalyticsEvents.ORDER_OPENED, { orderId, customerId: customer.id, customerName: customer.name });
    push(generatePath(ActoRoutes.CUSTOMER_ORDER_DETAILS, { id: customer.id, orderId }));
  };

  // The list and total own their reload states, so the page loader covers the first paint only.
  const isOrdersLoading = items.length === 0 && isPending;

  return (
    <PageLayout
      hPadding="card"
      isLoading={customerPending}
      isEmpty={!customer && !customerPending}
      emptyState={t("customerNotFound")}
    >
      <ShellSlotContent slot="topBarTrailing" id="ordersYearFilter">
        {yearOptions.length > 1 ? (
          <YearFilterPill selectedYear={effectiveYear} yearOptions={yearOptions} onChange={setSelectedYear} />
        ) : null}
      </ShellSlotContent>
      <Card>
        <SectionHeading>{t("ordersOverview.title")}</SectionHeading>
        <Divider />
        <div style={{ display: "flex", flexDirection: "column", gap: 4 }}>
          <MetaLabel>
            {t("ordersOverview.totalValue")} {effectiveYear}
          </MetaLabel>
          {isLoading ? (
            <Skeleton.Line width="55%" height={40} />
          ) : (
            <KpiHeadline>{formattedTotalIncomingOrder}</KpiHeadline>
          )}
        </div>
      </Card>

      <Card style={isOrdersLoading || isEmpty ? undefined : { paddingBottom: 0 }}>
        {isOrdersLoading ? (
          <RowSkeletonList />
        ) : isEmpty ? (
          <RowListEmpty>{t("ordersOverview.empty", { year: effectiveYear })}</RowListEmpty>
        ) : (
          <OrderRows items={items} onOpenOrder={(order) => handleOpenOrder(order.id)} sentinelRef={sentinelRef} />
        )}
      </Card>
    </PageLayout>
  );
}
